import React, { useMemo, useState, useEffect } from 'react';
import { makeStyles, Input, Button, tokens, Field, SelectionEvents, OptionOnSelectData } from "@fluentui/react-components";
import { Server } from '../apis';
import { useNavigate, useParams } from 'react-router';
import { ICompany, ICustomer } from '../IDB';
import { DropdownWithCreateButton } from '../components/DropdownWithCreateButton';

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        maxWidth: "400px",
        marginLeft: "260px", // Width of navbar
        padding: tokens.spacingVerticalXXL
    },
    header: {
        marginTop: '0px',
    },
    field: {
        marginBottom: tokens.spacingVerticalS
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    label: {
        fontWeight: "bold",
        fontSize: "14px",
        color: "#333",
        marginBottom: "8px",
    },
    input: {
        border: "1px solid #ccc",
        fontSize: "14px",
        color: "#333",
        outline: "none",
        transition: "border-color 0.2s ease",
    },
    buttonGroup: {
        display: "flex",
        gap: "12px",
        marginTop: "24px",
    },
    buttonHover: {
        backgroundColor: "#005a9e",
    },
    error: {
        color: "red",
        fontSize: "14px",
        marginTop: "8px",
    },
    success: {
        color: "green",
        fontSize: "14px",
        marginTop: "8px",
    },
    buttonDelete: {
        color: 'red',
        border: '1px solid red',
    }
});

interface IAddCustomerProps {
    companies: ICompany[];
    refresh: (cb?: () => void) => void;
}

export const AddCustomer = (props: IAddCustomerProps) => {
    const styles = useStyles();
    const server = useMemo(() => Server.getInstance(), []);
    const navigate = useNavigate();
    const params = useParams();

    const [existingCustomer, setExistingCustomer] = useState<ICustomer>();
    useEffect(() => {
        if (params.customer_id) {
            server.getCustomer(params.customer_id).then((customer) => setExistingCustomer(customer));
        }
    }, [params.customer_id]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [companyError, setCompanyError] = useState<string | null>(null);
    const [formData, setFormData] = useState<{name: string, email: string, company: number, phone: string, address: string}>({
        name: existingCustomer ? existingCustomer.name : '',
        email: existingCustomer ? existingCustomer.email : '',
        company: existingCustomer ? existingCustomer.company.id : 0,
        phone: existingCustomer && existingCustomer.phone ? existingCustomer.phone : '',
        address: existingCustomer && existingCustomer.address ? existingCustomer.address : '',
    });
    const [newCompany, setNewCompany] = useState<{ id: number, name: string }>();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [event.target.name]: event.target.value
        });
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        setEmailError(null); // Clear previous errors before starting new submission

        try {
            if (newCompany && newCompany.id === -1 && (formData.company === -1 || formData.company === 0)) {
                // Don't want to create the company if user went through the Create New prompt but ended up selecting an existing company from the dropdown
                const newCompanyId = await server.createCompany(newCompany.name);
                formData.company = newCompanyId;
            }
            
            const updateCustomer = [];
            const addCustomer = [];
            if (existingCustomer) {
                await server.updateCustomer(existingCustomer.id, formData);
                updateCustomer.push(existingCustomer.id);
            } else {
                const newCustomer = await server.createCustomer(formData);
                addCustomer.push(newCustomer);
            }
            navigate(`/customers/`);
        } catch (error) {
            // Check if error is a 400 status error (assuming the error contains a response with a status)
            if (error && error.status === 409) {
                if (error.response.data.detail.indexOf("Customer could not be created due to a uniqueness constraint") > -1) {
                    setEmailError("Another customer has already been registered with this email address.");
                } else if (error.response.data.detail.indexOf("Company could not be created due to a uniqueness constraint") > -1) {
                    setCompanyError("Another company has already been registered with that name.");
                }
            }
        } finally {
            setIsSubmitting(false); // Ensure that submitting state is always reset
        }
    };

    const deleteCustomer = async () => {
        window.confirm("Are you sure you want to delete this customer? This action cannot be undone.") && server.deleteCustomer(existingCustomer!.id).then(() => {
            navigate(`/customers/`);
        }).catch((error) => {
            console.error("Error deleting customer:", error);
            alert("An error occurred while trying to delete the customer. Please try again.");
        });
    };

    const onSelectedCompany = (event: SelectionEvents, data: OptionOnSelectData) => {
        if (data.optionValue) {
            setFormData({
                ...formData,
                company: parseInt(data.optionValue)
            });
        }
    };

    return (
        <form key={existingCustomer?.id} className={styles.root} onSubmit={handleSubmit}>
            <h4 className={styles.header}>{existingCustomer ? "Update" : "Add"} Customer</h4>
            <Field label="Company" required className={styles.field} validationMessage={companyError ? companyError : null} validationState={companyError ? 'error' : 'none'}>
                <DropdownWithCreateButton
                    defaultOption={existingCustomer ? { key: `${existingCustomer.company.id}`, text: existingCustomer.company.name } : { key: '', text: '' }}
                    onCreateNew={setNewCompany}
                    onOptionSelect={onSelectedCompany}
                    options={props.companies.map(company => ({ key: company.id.toString(), text: company.name }))}
                    width={'400px'} // Should match root maxWidth above
                />
            </Field>

            <Field label="Recipient" required className={styles.field}>
                <Input
                    value={formData.name}
                    name="name"
                    onChange={handleChange}
                />
            </Field>

            <Field label="Email" required className={styles.field} validationMessage={emailError ? emailError : null} validationState={emailError ? 'error' : 'none'}>
                <Input
                    value={formData.email}
                    name="email"
                    onChange={handleChange}
                    required
                    type="email"
                    {...props}
                />
            </Field>

            <Field label="Phone number" className={styles.field}>
                <Input
                    value={formData.phone}
                    name="phone"
                    onChange={handleChange}
                    {...props}
                />
            </Field>

            <Field label="Address" className={styles.field}>
                <Input
                    value={formData.address}
                    name="address"
                    onChange={handleChange}
                    {...props}
                />
            </Field>

            <div className={styles.buttonGroup}>
                <Button
                    appearance="primary"
                    type="submit"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : (existingCustomer ? 'Update' : 'Submit')}
                </Button>
                {existingCustomer ?
                    <Button
                        className={styles.buttonDelete}
                        appearance="outline"
                        onClick={deleteCustomer}
                    >
                        Delete
                    </Button>
                    : null}
            </div>
        </form>
    );
};
